import React from "react";

export default function MessagePanel({ 
  show, 
  type = "success", 
  title, 
  message, 
  subMessage, 
  onClose,
  onCallback,
  autoClose = true,
  duration = 3000 
}) {
  
  React.useEffect(() => {
    if (show && autoClose) {
      const timer = setTimeout(() => {
        if (onClose) {
          onClose();
        }
        if (onCallback) {
          onCallback();
        }
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [show, autoClose, onClose, onCallback, duration]);

  if (!show) return null;

  const configs = {
    success: {
      bgColor: "bg-green-500/20",
      borderColor: "border-green-500/30",
      iconColor: "text-green-400",
      titleColor: "text-green-400",
      dotColor: "bg-green-500",
      icon: (
        <svg
          className="w-12 h-12"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="3"
            d="M5 13l4 4L19 7"
          ></path>
        </svg>
      )
    },
    error: {
      bgColor: "bg-red-500/20",
      borderColor: "border-red-500/30",
      iconColor: "text-red-400",
      titleColor: "text-red-400",
      dotColor: "bg-red-500",
      icon: (
        <svg
          className="w-12 h-12"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="3"
            d="M6 18L18 6M6 6l12 12"
          ></path>
        </svg>
      )
    },
    warning: {
      bgColor: "bg-yellow-500/20",
      borderColor: "border-yellow-500/30",
      iconColor: "text-yellow-400",
      titleColor: "text-yellow-400",
      dotColor: "bg-yellow-500",
      icon: (
        <svg
          className="w-12 h-12"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="3"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          ></path>
        </svg>
      )
    },
    info: {
      bgColor: "bg-cyan-500/20",
      borderColor: "border-cyan-500/30",
      iconColor: "text-cyan-400",
      titleColor: "text-cyan-400",
      dotColor: "bg-cyan-500",
      icon: (
        <svg
          className="w-12 h-12"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="3"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          ></path>
        </svg>
      )
    }
  };

  const config = configs[type] || configs.success;

  return (
    <div className="absolute inset-0 flex items-center justify-center z-[100] backdrop-blur-sm bg-black/40 animate-enter">
      <div className={`glass rounded-3xl p-8 max-w-sm mx-4 text-center shadow-2xl border-2 ${config.borderColor} animate-enter`}>
        <div className="mb-6 flex justify-center">
          <div className={`w-20 h-20 rounded-full ${config.bgColor} flex items-center justify-center border-4 border-${type}-500 animate-pulse ${config.iconColor}`}>
            {config.icon}
          </div>
        </div>
        
        <h3 className={`text-2xl font-bold ${config.titleColor} mb-2`}>
          {title}
        </h3>
        
        {message && (
          <p className="text-muted mb-1">
            {message}
          </p>
        )}
        
        {subMessage && (
          <p className="text-xs font-mono text-accent">
            {subMessage}
          </p>
        )}
        
        {autoClose && (
          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-muted">
            <div className={`w-2 h-2 ${config.dotColor} rounded-full animate-pulse`}></div>
            <span>{type === "error" ? "Auto-closing..." : "Redirecting..."}</span>
          </div>
        )}

        {onClose && !autoClose && (
          <button
            onClick={onClose}
            className={`mt-6 px-6 py-2 rounded-xl border ${config.borderColor} ${config.titleColor} font-semibold hover:${config.bgColor} transition-colors`}
          >
            Close
          </button>
        )}
 

      </div>
    </div>
  );
}
